import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heartbeat, ArrowRight } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";
import { scoreBandClass, scoreBandLabel, formatDate, formatApiError } from "../lib/utils-extra";

const ACTION_BADGE = {
  not_started: "bg-[#F1F5F9] text-[#475569]",
  in_progress: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  completed: "bg-[#16A34A]/10 text-[#16A34A]",
  escalated: "bg-[#DC2626]/10 text-[#DC2626]",
};

function suggestedAction(score) {
  if (score >= 95) return "Recognition / Reward";
  if (score >= 85) return "Maintain Performance";
  if (score >= 75) return "Normal Monitoring";
  if (score >= 60) return "Coaching Required";
  return "Performance Improvement Plan / Escalation";
}

export default function Coaching() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    const { data } = await api.get("/coaching");
    setList(data);
    setSelected((cur) => (cur ? data.find((e) => e.id === cur.id) || cur : cur));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(`/coaching/${selected.id}`, {
        coaching_plan: selected.coaching_plan,
        corrective_actions: selected.corrective_actions,
        follow_up_date: selected.follow_up_date || null,
        action_status: selected.action_status,
        strengths: selected.strengths,
        areas_for_improvement: selected.areas_for_improvement,
      });
      toast.success("Coaching saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="coaching-page">
      <div>
        <div className="small-label">People</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Coaching & Improvement</h1>
        <p className="text-[#475569] text-sm mt-1">Track action plans tied to finalized evaluations.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3 lg:col-span-1">
          {list.length === 0 && (
            <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-6 text-sm text-[#475569]">
              No finalized evaluations yet.
            </CardContent></Card>
          )}
          {list.map((e) => (
            <Card
              key={e.id}
              onClick={() => setSelected(e)}
              className={`surface-card border-[#E2E8F0] cursor-pointer ${selected?.id === e.id ? "ring-2 ring-[#0EA5E9]" : ""}`}
              data-testid={`coaching-item-${e.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-base font-semibold">{e.employee_name}</div>
                    <div className="text-xs text-[#475569]">{e.position_name} · {formatDate(e.evaluation_date)}</div>
                  </div>
                  <Badge className={ACTION_BADGE[e.action_status || "not_started"] + " border-0"}>
                    {(e.action_status || "not_started").replace("_", " ")}
                  </Badge>
                </div>
                <div className={`mt-3 px-3 py-2 rounded-lg ${scoreBandClass(e.final_score || 0)} text-white text-xs flex justify-between`}>
                  <span>{scoreBandLabel(e.final_score || 0)}</span>
                  <span>{(e.final_score || 0).toFixed(1)}%</span>
                </div>
                <div className="mt-2 text-xs text-[#475569] flex items-center gap-1">
                  <Heartbeat size={12} weight="duotone" /> Suggested: {suggestedAction(e.final_score || 0)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <Card className="surface-card border-[#E2E8F0]" data-testid="coaching-detail">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="small-label">Plan for</div>
                    <div className="font-display text-xl font-semibold">{selected.employee_name}</div>
                  </div>
                  <Link to={`/evaluations/${selected.id}`}>
                    <Button variant="outline" size="sm">Open eval <ArrowRight size={14} className="ml-1" /></Button>
                  </Link>
                </div>

                <Area label="Strengths" value={selected.strengths || ""} onChange={(v) => setSelected({ ...selected, strengths: v })} testid="coach-strengths" />
                <Area label="Areas for Improvement" value={selected.areas_for_improvement || ""} onChange={(v) => setSelected({ ...selected, areas_for_improvement: v })} testid="coach-improvement" />
                <Area label="Coaching Plan" value={selected.coaching_plan || ""} onChange={(v) => setSelected({ ...selected, coaching_plan: v })} testid="coach-plan" />
                <Area label="Corrective Actions" value={selected.corrective_actions || ""} onChange={(v) => setSelected({ ...selected, corrective_actions: v })} testid="coach-actions" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="small-label">Follow-up Date</Label>
                    <Input type="date" className="mt-2" value={selected.follow_up_date || ""}
                           onChange={(e) => setSelected({ ...selected, follow_up_date: e.target.value })} data-testid="coach-followup" />
                  </div>
                  <div>
                    <Label className="small-label">Status</Label>
                    <select
                      className="mt-2 w-full h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                      value={selected.action_status || "not_started"}
                      onChange={(e) => setSelected({ ...selected, action_status: e.target.value })}
                      data-testid="coach-status"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="escalated">Escalated</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={save} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-coaching-button">Save plan</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-10 text-center text-sm text-[#475569]">
              Pick someone from the list to start coaching.
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="small-label">{label}</Label>
      <Textarea rows={3} className="mt-2" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
